import Table from 'components/Table';
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getPayrolls, reopenPayroll, deletePayroll, voidPayroll } from 'services/payrolls';
import ConfirmModal from 'components/ConfirmModal';
import { useNavigate } from 'react-router-dom';
import PayrollPreviewModal from './PayrollPreviewModal';
import ContractorPaymentsModal from './ContractorPaymentsModal';
import W2EmployeesModal from './W2EmployeesModal';
import OffCyclePayrollModal from './OffCyclePayrollModal';
import CorrectionPayrollModal from './CorrectionPayrollModal';

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
};

const fmt = (n) =>
    n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PayrollsTable = ({ searchQuery = '', showAmendments = false, statusFilter = 'all' }) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [previewPayroll, setPreviewPayroll] = useState(null);
    const [offCyclePayroll, setOffCyclePayroll] = useState(null);
    const [correctionPayroll, setCorrectionPayroll] = useState(null);
    const [contractorPayroll, setContractorPayroll] = useState(null);
    const [w2EmployeePayroll, setW2EmployeePayroll] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const { data: response, isLoading } = useQuery({
        queryKey: ['payrolls'],
        queryFn: getPayrolls,
    });

    const { mutate: doDelete } = useMutation({
        mutationFn: deletePayroll,
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['payrolls'] }); setConfirmDelete(null); },
    });

    const { mutate: reopen } = useMutation({
        mutationFn: reopenPayroll,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payrolls'] }),
    });

    const [confirmVoid, setConfirmVoid] = useState(null);
    const [voidError, setVoidError] = useState('');
    const { mutate: doVoid } = useMutation({
        mutationFn: voidPayroll,
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['payrolls'] }); setConfirmVoid(null); setVoidError(''); },
        onError: (err) => setVoidError(err?.response?.data?.message || err?.message || 'Failed to void payroll'),
    });

    const payrolls = useMemo(() => {
        let list = Array.isArray(response) ? response : (response?.data ?? []);
        list = [...list].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        // Amendment/correction filter
        if (!showAmendments) {
            list = list.filter(p => p.type !== 'correction');
        }

        // Status filter
        if (statusFilter && statusFilter !== 'all') {
            list = list.filter(p => (p.status || '').toLowerCase() === statusFilter);
        }

        // Search
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            list = list.filter(p => {
                const period = `${p.startDate || ''} ${p.endDate || ''}`.toLowerCase();
                const type   = (p.type || '').toLowerCase();
                const status = (p.status || '').toLowerCase();
                const payDate = (p.payDate || '').toLowerCase();
                return period.includes(q) || type.includes(q) || status.includes(q) || payDate.includes(q);
            });
        }

        return list;
    }, [response, searchQuery, showAmendments, statusFilter]);

    const TYPE_LABELS = { regular: 'Regular', off_cycle: 'Off-Cycle', correction: 'Correction', '1099_only': '1099 Only' };
    const TYPE_COLORS = { regular: '#0d6efd', off_cycle: '#6f42c1', correction: '#fd7e14', '1099_only': '#198754' };

    const columns = useMemo(() => [
        {
            key: 'pay_period',
            headerName: 'Pay Period',
            renderValue: (row) => `${formatDate(row.startDate)} – ${formatDate(row.endDate)}`,
        },
        {
            key: 'payDate',
            headerName: 'Pay Date',
            renderValue: (row) => formatDate(row.payDate),
        },
        {
            key: 'type',
            headerName: 'Type',
            renderValue: (row) => {
                const t = row.type || 'regular';
                return (
                    <span style={{
                        background: TYPE_COLORS[t] + '22',
                        color: TYPE_COLORS[t],
                        border: `1px solid ${TYPE_COLORS[t]}55`,
                        borderRadius: 20,
                        padding: '2px 10px',
                        fontSize: 12,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                    }}>
                        {TYPE_LABELS[t] || t}
                    </span>
                );
            },
        },
        {
            key: 'status',
            headerName: 'Status',
            renderValue: (row) => (
                <button className={row.status === 'approved' ? 'approved-button' : 'draft-button'}>
                    {row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : 'Draft'}
                </button>
            ),
        },
        {
            key: 'grossPay',
            headerName: 'Gross Pay',
            renderValue: (row) => fmt(row.grossPay),
        },
        {
            key: 'totalTaxes',
            headerName: 'Total Taxes',
            renderValue: (row) => fmt(row.totalTaxes),
        },
        {
            key: 'netPay',
            headerName: 'Net Pay',
            renderValue: (row) => fmt(row.netPay),
        },
        {
            key: 'actions',
            headerName: 'Actions',
            renderValue: (row) => (
                <div className='d-flex gap-2 flex-wrap'>
                    {row.status === 'draft' && (
                        <>
                            {(row.type === 'regular' || !row.type) && (
                                <button className='button-orange px-3 py-1' style={{ fontSize: '12px' }} onClick={() => setContractorPayroll(row)}>
                                    Add/Update Contractors
                                </button>
                            )}
                            {(row.type === 'regular' || !row.type) && (
                                <button className='button-orange px-3 py-1' style={{ fontSize: '12px' }} onClick={() => setPreviewPayroll(row)}>
                                    Preview & Approve
                                </button>
                            )}
                            {row.type === '1099_only' && (
                                <button className='button-orange px-3 py-1' style={{ fontSize: '12px' }} onClick={() => setW2EmployeePayroll(row)}>
                                    Add/Update W-2 Employees
                                </button>
                            )}
                            {row.type === '1099_only' && (
                                <button className='button-orange px-3 py-1' style={{ fontSize: '12px' }} onClick={() => setPreviewPayroll(row)}>
                                    Preview & Approve
                                </button>
                            )}
                            {row.type === 'off_cycle' && (
                                <button className='button-orange px-3 py-1' style={{ fontSize: '12px' }} onClick={() => setOffCyclePayroll(row)}>
                                    Enter Payments
                                </button>
                            )}
                            {row.type === 'correction' && (
                                <button className='button-orange px-3 py-1' style={{ fontSize: '12px' }} onClick={() => setCorrectionPayroll(row)}>
                                    Enter Correction
                                </button>
                            )}
                            <button className='btn btn-sm btn-outline-danger' style={{ fontSize: '12px' }} onClick={() => setConfirmDelete(row)}>
                                Delete
                            </button>
                        </>
                    )}
                    {row.status === 'approved' && (
                        <>
                            <button
                                className='button-orange px-3 py-1'
                                style={{ fontSize: '12px' }}
                                onClick={() => navigate(`/dashboard/payroll/${row.id}`)}
                            >
                                View
                            </button>
                            <button
                                className='cancel-button px-3 py-1'
                                style={{ fontSize: '12px' }}
                                onClick={() => reopen(row.id)}
                            >
                                Reopen
                            </button>
                            <button
                                className='btn btn-sm btn-outline-danger'
                                style={{ fontSize: '12px' }}
                                onClick={() => setConfirmVoid(row)}
                            >
                                Void
                            </button>
                        </>
                    )}
                    {row.status === 'voided' && (
                        <span style={{ color: '#6b7280', fontSize: 12, fontStyle: 'italic' }}>Voided</span>
                    )}
                </div>
            ),
        },
    ], [navigate, reopen, doVoid]);

    if (isLoading) {
        return <div className='p-4 text-center'>Loading payrolls...</div>;
    }

    if (payrolls.length === 0) {
        return (
            <div className='p-5 text-center'>
                <p className='description'>No payrolls yet. Create your first payroll to get started.</p>
                <button className='button-orange mt-3 px-4' onClick={() => navigate('/dashboard/start-new-payroll')}>
                    Start New Payroll
                </button>
            </div>
        );
    }

    return (
        <>
            <Table
                headers={columns}
                data={payrolls}
                showPagination={false}
                containerStyle={{ borderRadius: '0px', border: 'none' }}
            />
            {previewPayroll && (
                <PayrollPreviewModal
                    payroll={previewPayroll}
                    onClose={() => setPreviewPayroll(null)}
                    onApproved={() => setPreviewPayroll(null)}
                />
            )}
            {contractorPayroll && (
                <ContractorPaymentsModal
                    payroll={contractorPayroll}
                    onClose={() => setContractorPayroll(null)}
                />
            )}
            {w2EmployeePayroll && (
                <W2EmployeesModal
                    payroll={w2EmployeePayroll}
                    onClose={() => setW2EmployeePayroll(null)}
                />
            )}
            {offCyclePayroll && (
                <OffCyclePayrollModal
                    payroll={offCyclePayroll}
                    onClose={() => setOffCyclePayroll(null)}
                    onApproved={() => { setOffCyclePayroll(null); queryClient.invalidateQueries({ queryKey: ['payrolls'] }); }}
                />
            )}
            {correctionPayroll && (
                <CorrectionPayrollModal
                    payroll={correctionPayroll}
                    onClose={() => setCorrectionPayroll(null)}
                    onApproved={() => { setCorrectionPayroll(null); queryClient.invalidateQueries({ queryKey: ['payrolls'] }); }}
                />
            )}
            {confirmVoid && (
                <ConfirmModal
                    show={!!confirmVoid}
                    title="Void Payroll"
                    message={voidError
                        ? `Error: ${voidError}`
                        : `Are you sure you want to void the payroll for ${confirmVoid.startDate ? new Date(confirmVoid.startDate).toLocaleDateString() : 'this period'}? This cannot be undone. A voided payroll is permanently marked as invalid.`}
                    confirmText="Void Payroll"
                    onConfirm={() => { setVoidError(''); doVoid(confirmVoid.id); }}
                    onCancel={() => { setConfirmVoid(null); setVoidError(''); }}
                    danger
                />
            )}
            {confirmDelete && (
                <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={() => setConfirmDelete(null)}>
                    <div className="modal-dialog" onClick={e => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Delete Payroll</h5>
                                <button type="button" className="btn-close" onClick={() => setConfirmDelete(null)} />
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to delete this payroll? This action cannot be undone.</p>
                                <p className="text-muted" style={{ fontSize: 13 }}>Period: {formatDate(confirmDelete.startDate)} — {formatDate(confirmDelete.endDate)}</p>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setConfirmDelete(null)}>Cancel</button>
                                <button className="btn btn-danger" onClick={() => doDelete(confirmDelete.id)}>Delete</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PayrollsTable;




